import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// `formatCurrency` / `formatDate` delegate to the locale-aware helpers in
// `./format` so the active i18n language drives formatting. Existing callers
// keep their signatures; passing a currency or `Intl.DateTimeFormatOptions`
// works exactly as before.
import { formatPrice as _formatPrice, formatDate as _formatDate, formatDateShort as _formatDateShort } from './format';

export function formatCurrency(amount: number, currency = 'USD'): string {
  return _formatPrice(amount, { currency });
}

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  return _formatDate(date, options);
}

export function formatDateShort(date: string | Date): string {
  return _formatDateShort(date);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength).trimEnd() + '...';
}

export function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

// Renders a "was X → Y" diff for an agent approval proposed change. Returns
// just "→ Y" when oldVal is missing so callers don't need to branch.
export function formatApprovalDiff(field: string, oldVal: unknown, newVal: unknown): string {
  const f = field.toLowerCase();
  const fmt = (v: unknown): string => {
    if (v == null) return '—';
    if (typeof v === 'number') {
      if (f.includes('price') || f.includes('amount') || f.includes('cost')) return formatCurrency(v);
      if (f.includes('percent') || f.includes('margin') || f.includes('discount')) return `${v}%`;
      if (f.includes('inventory') || f.includes('stock') || f.includes('qty') || f.includes('quantity')) return `${v} units`;
      return String(v);
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };
  const newStr = fmt(newVal);
  if (oldVal == null) return `→ ${newStr}`;
  return `was ${fmt(oldVal)} → ${newStr}`;
}
